import { useStorage } from '@vueuse/core'
import type { Call, CallCenterPreferences, CallCenterState } from '~/types/call'
import { nativeWindow, platform, screen, webContents } from '@todesktop/client-core'

export function useCallCenter() {
    const config = useRuntimeConfig()
    const auth = useAuthStore()
    const state = useStorage('call-center', {} as CallCenterState)
    const pbx = usePbx()

    const heartbeatInterval = ref<NodeJS.Timeout>()

    function resetState() {
        state.value = { preferences: state.value.preferences } as CallCenterState
        clearInterval(heartbeatInterval.value)
    }

    function initCallCenter() {
        console.debug('[DEBUG] useCallCenter.ts initCallCenter() - Initializing call center', {
            isDesktopApp: platform.todesktop.isDesktopApp(),
            hasPreferences: !!state.value.preferences,
            extension: state.value.preferences?.extension
        })

        if (platform.todesktop.isDesktopApp()) {
            console.debug('[DEBUG] useCallCenter.ts initCallCenter() - Setting up window open rules for desktop app')
            webContents.setWindowOpenRules({
                /// @ts-ignore
                ref: undefined,
                rules: [],
                fallback: {
                    action: 'deny',
                },
            })
        }

        if (state.value.preferences?.extension) {
            console.debug('[DEBUG] useCallCenter.ts initCallCenter() - Starting extension status monitoring')
            fetchExtensionStatus()
            heartbeatInterval.value = setInterval(() => {
                console.debug('[DEBUG] useCallCenter.ts initCallCenter() - Running periodic extension status check')
                fetchExtensionStatus()
            }, 30000)
        }

        console.debug('[DEBUG] useCallCenter.ts initCallCenter() - Call center initialization complete')
    }

    async function joinCall(call: Call) {
        if (platform.todesktop.isDesktopApp()) {
            if (state.value.activeCallId) {
                const activeWindow = (await nativeWindow.getAllWindows()).find((window) => window.id === state.value.activeWindowId)
                if (!activeWindow) {
                    state.value.activeCallId = undefined
                    state.value.activeWindowId = undefined
                }
                else {
                    const cursor = await screen.getCursorScreenPoint()
                    const bounds = (await screen.getDisplayNearestPoint(cursor)).bounds
                    const [width, height] = await nativeWindow.getSize({ ref: activeWindow })
                    await nativeWindow.setPosition(
                        { ref: activeWindow },
                        Math.round(bounds.x + (bounds.width - width) / 2),
                        Math.round(bounds.y + (bounds.height - height) / 2),
                    )

                    await nativeWindow.show({ ref: activeWindow })
                }
            }

            if (!state.value.activeCallId) {
                const activeCallWindow = await nativeWindow.create({
                    center: true,
                    fullscreenable: true,
                    maximizable: true,
                    minimizable: false,
                    autoHideMenuBar: true,
                    resizable: true,
                    height: 1028,
                    width: 1440,
                    backgroundColor: '#111827',
                    titleBarStyle: 'hiddenInset',
                })

                state.value.activeCallId = call.id
                state.value.activeWindowId = activeCallWindow.id

                webContents.loadURL({ ref: activeCallWindow }, `${config.public.appUrl}/call/${call.id}`)
            }
        }
        else {
            navigateTo(`/call/${call.id}`, { open: { target: '_blank' } })
        }
    }

    async function fetchExtensionStatus(reset: boolean = false): Promise<void> {
        const preferences = state.value.preferences

        if (preferences?.extension) {
            try {
                if (!preferences?.extensionToken || reset) {
                    preferences.extensionToken = await pbx.login()
                }
                else {
                    await pbx.heartbeat(preferences.extensionToken)
                }

                if (auth.user?.id) {
                    await useSupabaseClient()
                        .from('users')
                        .update({
                            metadata: {
                                ...auth.user.metadata,
                                extension: preferences.extension
                            }
                        } as never)
                        .eq('id', auth.user?.id)
                }
            } catch {
                preferences.extensionToken = undefined
            }
        }
    }

    async function fetchExtensionCaller(): Promise<string | undefined> {
        const preferences = state.value.preferences
        if (preferences?.extension && preferences.extensionToken) {
            try {
                const response = await pbx.queryExtension(preferences.extensionToken, preferences.extension)
                if (response?.from) {
                    return response.from
                }
            } catch { }
        }
    }

    function getPreferences() {
        return state.value.preferences
    }

    function updatePreferences(preferences: CallCenterPreferences) {
        state.value.preferences = preferences
    }

    return {
        state,
        resetState,
        initCallCenter,
        joinCall,
        fetchExtensionStatus,
        fetchExtensionCaller,
        getPreferences,
        updatePreferences,
    }
}
